/**
 * Formats a value as a currency string using `Intl.NumberFormat`.
 *
 * @param params - The AG Grid value formatter parameters containing the value to format.
 * @param options - Optional `Intl.NumberFormatOptions` to customize formatting (e.g., currency, fraction digits).
 * @returns The formatted currency string, or an empty string if the value is invalid.
 *
 * @example
 * ```typescript
 * // Usage in AG Grid column definition:
 * {
 *   field: 'amount',
 *   valueFormatter: (params) => currency(params, { currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
 * }
 * ```
 *
 * @see {@link https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat | Intl.NumberFormat documentation}
 */
import { ValueFormatterParams } from "ag-grid-community";

export const currency =
  (options: Intl.NumberFormatOptions = {}) =>
  (params: ValueFormatterParams): string => {
    const numberValue = Number(params.value);
    if (isNaN(numberValue)) return "";
    const { currency, minimumFractionDigits, maximumFractionDigits, ...rest } =
      options;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
      ...rest,
    }).format(numberValue);
  };
